import React from "react";
import {
  PageComponentsDynamicZone,
  FooterComponentsDynamicZone,
  Maybe,
  MenuItemComponentsDynamicZone,
  ZoneComponentsDynamicZone,
  MenuComponentsDynamicZone,
} from "../graphql/generated/graphql";
import { ILinkListComponentConfiguration } from "../components/navigation/LinkList/types";
import { IVideoComponentConfiguration } from "./sections/Video/types";
import { getSiteConfig } from "../siteConfig";
import { getComponentMap } from "../componentMap";

interface IComponentRenderer {
  components:
    | PageComponentsDynamicZone[]
    | Maybe<FooterComponentsDynamicZone>[]
    | Maybe<MenuItemComponentsDynamicZone>[]
    | Maybe<ZoneComponentsDynamicZone>[]
    | Maybe<MenuComponentsDynamicZone>[]
    | null
    | any[];
  componentConfiguration?: IComponentConfiguration;
  componentMapOverrides?: any;
  componentMap?: any;
}

type IComponentConfigurationBase = {
  Wrapper?: React.FC<{}>;
};

type IComponentConfiguration = {
  ComponentNavigationLinkList?: IComponentConfigurationBase &
    ILinkListComponentConfiguration;
  ComponentSectionsFeatureGrid?: IComponentConfigurationBase;
  ComponentSectionsInfoBox?: IComponentConfigurationBase;
  ComponentNavigationNavbar?: IComponentConfigurationBase;
  ComponentSectionsPagePreviewList?: IComponentConfigurationBase;
  ComponentSectionsVideo?: IVideoComponentConfiguration &
    IComponentConfigurationBase;
} & { [key: string]: any };

function Component({
  component,
  componentConfiguration,
  componentMap,
}: {
  component:
    | PageComponentsDynamicZone
    | Maybe<FooterComponentsDynamicZone>
    | Maybe<MenuItemComponentsDynamicZone>;
  componentConfiguration?: IComponentConfiguration;
  componentMap?: any;
}) {
  if (!component) return <p>Empty component passed</p>;
  if (!componentMap) return <p>componentMap cannot be undefined</p>;

  const { __typename, ...componentProps } = component;

  if (!__typename) return <p>__typename cannot be undefined</p>;
  if (__typename === "Error") return <p>__typename equals Error</p>;

  // not each __typename is within the type of IComponentConfiguration
  let configuration: any = componentConfiguration?.[__typename];
  if (__typename === "ComponentSectionsZoneContainer") {
    configuration = componentConfiguration;
  }

  // @ts-ignore
  const componentKey = `${__typename}_${component.id}`;

  if (componentMap[__typename]) {
    const Comp = componentMap[__typename];

    if (configuration?.Wrapper) {
      return (
        <configuration.Wrapper key={componentKey}>
          <Comp
            {...componentProps}
            __typename={__typename}
            configuration={configuration}
          />
        </configuration.Wrapper>
      );
    }

    return (
      // @ts-ignore
      <Comp
        key={componentKey}
        {...componentProps}
        configuration={configuration}
        __typename={__typename}
      />
    );
  }

  return (
    <p key={componentKey}>
      {`Component ${__typename} was used but has not been implemented
        yet.`}
    </p>
  );
}

export function ComponentRenderer({
  components,
  componentConfiguration,
  componentMap,
}: IComponentRenderer) {
  if (!components || components.length === 0) {
    return <p>No components to render</p>;
  }

  return (
    <>
      {components.map((c) => {
        if (!c.__typename) return <p>no __typename</p>;
        if (!c.id) return <p>no id</p>;

        return (
          <Component
            key={`${c.__typename}-${c.id}`}
            component={c}
            componentConfiguration={componentConfiguration}
            componentMap={componentMap || getComponentMap()}
          />
        );
      })}
    </>
  );
}
